
/**
 * Форматирует номер телефона в формат E.164 для совместимости
 * @param phone Номер телефона для форматирования
 * @returns Форматированный номер телефона
 */
export const formatPhoneToE164 = (phone: string): string => {
  if (!phone) return '';
  
  // Убираем все не-цифровые символы
  const digitsOnly = phone.replace(/\D/g, '');
  
  // Если начинается с 8, заменяем на 7 (российский формат)
  let normalizedNumber = digitsOnly;
  if (digitsOnly.startsWith('8') && digitsOnly.length === 11) {
    normalizedNumber = '7' + digitsOnly.substring(1);
  }
  
  // Если номер начинается с 7 и содержит 11 цифр, добавляем +
  if (normalizedNumber.startsWith('7') && normalizedNumber.length === 11) {
    return '+' + normalizedNumber;
  }
  
  // Если номер уже с +, вернем как есть
  if (phone.startsWith('+')) {
    return phone;
  }
  
  // Иначе добавляем + перед номером
  return '+' + normalizedNumber;
};

/**
 * Генерирует все возможные варианты записи телефонного номера
 * для более эффективного поиска
 * @param phone Исходный телефонный номер
 * @returns Массив вариантов записи телефонного номера
 */
export const generatePhoneVariants = (phone: string): string[] => {
  if (!phone) return [];
  
  const variants = new Set<string>();
  
  // Добавляем оригинальный номер
  variants.add(phone);
  
  // Получаем нормализованный номер
  const normalizedE164 = formatPhoneToE164(phone);
  if (normalizedE164 && normalizedE164 !== phone) {
    variants.add(normalizedE164);
  }
  
  // Очищаем от всех не-цифровых символов
  const digitsOnly = phone.replace(/\D/g, '');
  variants.add(digitsOnly);
  
  // Если номер начинается с 8 (российский формат)
  if (digitsOnly.startsWith('8') && digitsOnly.length === 11) {
    // Вариант с 7 вместо 8
    const with7 = '7' + digitsOnly.substring(1);
    variants.add(with7);
    variants.add('+' + with7);
    
    // Добавляем вариант с 8
    variants.add('8' + digitsOnly.substring(1));
  }
  
  // Если номер начинается с 7 (российский формат с кодом страны)
  if (digitsOnly.startsWith('7') && digitsOnly.length === 11) {
    // Добавляем варианты с 7 и 8
    variants.add('7' + digitsOnly.substring(1));
    variants.add('8' + digitsOnly.substring(1));
    variants.add('+7' + digitsOnly.substring(1));
  }
  
  // Если номер начинается с +7 (международный формат для России)
  if (phone.startsWith('+7')) {
    // Убираем + и добавляем варианты с 7 и 8
    const withoutPlus = phone.substring(1);
    variants.add(withoutPlus);
    variants.add('8' + withoutPlus.substring(1));
  }
  
  // Возвращаем уникальные варианты
  return Array.from(variants);
};
